import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'moment-timezone';
import RouteService from '../../services/routeService';
import LocationService from '../../services/locationService';
import { getBaseCoords } from '../../util/locationUtils';
import { calculateDistance } from '../../util/geoUtils';
import { HOURS_PER_DAY, formatMinutes } from '../../util/durationDisplay';
import {
  todayInLocationTz,
  isDateAvailable,
  findNextAvailableDate,
  extractClosedDays,
  normalizeBlockedDates,
} from '../../util/dateAvailability';

// Mobile-mode appointment picker — date + 30-min slot grid.
//
// Shared across walk-in / in-app estimate / reschedule paths. Fetches slot
// availability from the server given the day's existing route + travel from
// the prior stop + travel back to the next stop. Admins can flip a "show
// all times" toggle to force-book outside the available slots with a warning.
//
// Props:
//   currentLocation  — mobile installer_location (required)
//   customerLat,     — customer's geocoded coords (required for slot calc;
//   customerLng        if missing, slot fetch is skipped and the grid renders
//                      every 30-min during operating hours with a notice)
//   services         — services array; used to compute durationMinutes
//   appointmentStart — moment / Date object currently selected (or null)
//   onAppointmentChange(moment) — fires when user picks a slot

const SLOT_MINUTES = 30;
const METERS_PER_MILE = 1609.344;

// Sum service durations in minutes. Mobile services use duration_unit='hours'
// per spec (duration_hours); fall back to days-based for safety.
const sumServiceDurationMinutes = (services) => {
  if (!Array.isArray(services) || services.length === 0) return SLOT_MINUTES;
  const total = services.reduce((sum, s) => {
    const unit = s.duration_unit || s.durationUnit;
    if (unit === 'hours') {
      const hours = Number(s.duration_hours ?? s.duration ?? 1);
      return sum + (Number.isFinite(hours) ? hours * 60 : 60);
    }
    // Days-based fallback — treat as full 8h working day
    const days = Number(s.duration_days ?? s.duration ?? 1);
    return sum + (Number.isFinite(days) ? days * HOURS_PER_DAY * 60 : HOURS_PER_DAY * 60);
  }, 0);
  // Round up to the next 30-min slot
  return Math.ceil(total / SLOT_MINUTES) * SLOT_MINUTES;
};

// Build a fallback slot grid client-side when the server endpoint isn't
// available yet OR the customer's coords are missing. Generates every
// 30-minute start time from open to (close - durationMinutes).
const buildFallbackSlots = (operatingHours, durationMinutes) => {
  const open = operatingHours?.open || '08:00';
  const close = operatingHours?.close || '17:00';
  const [openH, openM] = open.split(':').map(n => parseInt(n, 10));
  const [closeH, closeM] = close.split(':').map(n => parseInt(n, 10));
  const startMin = openH * 60 + openM;
  const endMin = closeH * 60 + closeM;
  const slots = [];
  for (let t = startMin; t + durationMinutes <= endMin; t += SLOT_MINUTES) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    slots.push({
      start_time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
      can_fit: true,
      fallback: true,
    });
  }
  return slots;
};

const formatTimeLabel = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

class MobileAppointmentForm extends Component {
  state = {
    selectedDate: null,                  // resolved in componentDidMount once we have location hours
    selectedSlot: this.props.appointmentStart
      ? moment(this.props.appointmentStart).format('HH:mm')
      : null,
    availability: null,
    loading: false,
    error: null,
    forceBook: false,
    // Location config — used to filter the date picker. Day-of-week numbers
    // (0=Sunday) where the location is closed, and specific YYYY-MM-DD dates
    // that are blocked (holidays / custom closures).
    closedDaysOfWeek: [],
    blockedDates: [],
    hoursLoaded: false,
  };

  componentDidMount() {
    this.loadLocationConfig();
  }

  // Operating hours + blocked dates determine which dates the picker offers.
  // Once loaded, we set the initial selectedDate to the next available date
  // (skipping closed weekdays and blocked dates) using the LOCATION's
  // timezone — not the browser's — so "today" matches the customer's clock.
  loadLocationConfig = async () => {
    const { currentLocation } = this.props;
    const locationId = currentLocation?.location_id;
    if (!locationId) {
      this.resolveInitialDate([], []);
      return;
    }
    try {
      const [hoursRes, blockedRes] = await Promise.all([
        LocationService.getOperatingHours(locationId).catch(() => ({ hours: [] })),
        LocationService.getBlockedDates(locationId).catch(() => ({ blockedDates: [] })),
      ]);
      const closedDaysOfWeek = extractClosedDays(hoursRes?.hours || hoursRes);
      const blockedDates = normalizeBlockedDates(blockedRes?.blockedDates || blockedRes);
      this.setState({ closedDaysOfWeek, blockedDates, hoursLoaded: true }, () => {
        this.resolveInitialDate(closedDaysOfWeek, blockedDates);
      });
    } catch {
      this.resolveInitialDate([], []);
    }
  };

  resolveInitialDate = (closedDaysOfWeek, blockedDates) => {
    const { currentLocation, appointmentStart } = this.props;
    const tz = currentLocation?.timezone || moment.tz.guess();
    if (appointmentStart) {
      this.setState(
        { selectedDate: moment.tz(appointmentStart, tz).startOf('day').toDate() },
        () => this.fetchAvailability()
      );
      return;
    }
    const next = findNextAvailableDate(todayInLocationTz(tz), closedDaysOfWeek, blockedDates);
    this.setState({ selectedDate: next.toDate() }, () => this.fetchAvailability());
  };

  componentDidUpdate(prevProps, prevState) {
    const dateChanged = prevState.selectedDate !== this.state.selectedDate;
    const coordsChanged =
      prevProps.customerLat !== this.props.customerLat ||
      prevProps.customerLng !== this.props.customerLng;
    if (dateChanged || coordsChanged) this.fetchAvailability();
  }

  fetchAvailability = async () => {
    const { currentLocation, customerLat, customerLng, services } = this.props;
    const { selectedDate } = this.state;
    if (!currentLocation?.location_id) return;
    if (!selectedDate) return;
    // Server requires lat/lng. Skip the call until coords arrive — re-fires
    // from componentDidUpdate when they do.
    if (customerLat == null || customerLng == null) return;

    const durationMinutes = sumServiceDurationMinutes(services);
    const dateStr = moment(selectedDate).format('YYYY-MM-DD');

    this.setState({ loading: true, error: null });
    try {
      const data = await RouteService.getMobileAvailability({
        locationId: currentLocation.location_id,
        date: dateStr,
        durationMinutes,
        customerLat,
        customerLng,
        // Reschedule context — when the form is opened against an existing
        // request, exclude that request from the conflict check so the
        // booking being modified doesn't block itself.
        excludeRequestId: this.props.excludeRequestId,
      });
      this.setState({ availability: data, loading: false });
    } catch (err) {
      // Server endpoint pending — degrade to a flat grid built from defaults.
      const status = err?.response?.status;
      this.setState({
        availability: null,
        loading: false,
        error: status === 404
          ? 'Availability check unavailable yet — pick any time within operating hours.'
          : 'Could not check availability.',
      });
    }
  };

  handleDateChange = (date) => {
    this.setState({ selectedDate: date, selectedSlot: null });
    this.props.onAppointmentChange && this.props.onAppointmentChange(null);
  };

  handleSlotPick = (slot) => {
    const { selectedDate } = this.state;
    const { currentLocation } = this.props;
    this.setState({ selectedSlot: slot.start_time });
    // Slot grid times are wall-clock at the LOCATION. Build the moment in
    // the location's timezone so "9:30 AM" means "9:30 AM at the location"
    // regardless of where the front desk is sitting. Falls back to the
    // browser's guessed zone if location.timezone isn't set.
    const tz = currentLocation?.timezone || moment.tz.guess();
    const dateStr = moment(selectedDate).format('YYYY-MM-DD');
    const dt = moment.tz(`${dateStr} ${slot.start_time}`, 'YYYY-MM-DD HH:mm', tz);
    this.props.onAppointmentChange && this.props.onAppointmentChange(dt);
  };

  toggleForceBook = () => {
    this.setState(prev => ({ forceBook: !prev.forceBook }));
  };

  // Distance from base to customer (informational only — the in-radius hard
  // check happened at the customer step).
  computeDistanceFromBase = () => {
    const { currentLocation, customerLat, customerLng } = this.props;
    const base = getBaseCoords(currentLocation);
    if (!base || customerLat == null || customerLng == null) return null;
    const meters = calculateDistance(customerLat, customerLng, base.lat, base.lng);
    return meters / METERS_PER_MILE;
  };

  render() {
    if (this.props.currentStep !== 5) return null;
    const { currentLocation, services } = this.props;
    const { selectedDate, selectedSlot, availability, loading, error, forceBook } = this.state;

    const durationMinutes = sumServiceDurationMinutes(services);
    const distanceFromBase = this.computeDistanceFromBase();

    const operatingHours = availability?.operating_hours || { open: '08:00', close: '17:00' };
    const serverSlots = availability?.slots || [];
    const fallbackGrid = buildFallbackSlots(operatingHours, durationMinutes);
    // Distinguish: server replied (respect it, even if empty) vs server errored
    // (fall back to operating-hours grid + notice). Force-book overrides either.
    const hasServerData = !!availability;
    const visibleSlots = forceBook
      ? fallbackGrid
      : hasServerData
      ? serverSlots
      : fallbackGrid;

    // "Today" anchored to the LOCATION's timezone — matches the customer's
    // clock. EDT viewer of an HST location uses HST today, not EDT today.
    const locationTz = currentLocation?.timezone || moment.tz.guess();
    const today = moment.tz(locationTz).startOf('day').toDate();

    return (
      <div className="mobile-appointment-form mt-4 mb-4">
        <h4 className="text-center mb-3">When should we come?</h4>

        {/* Service / location context */}
        <div className="text-center text-muted mb-3" style={{ fontSize: 13 }}>
          Service duration: <strong>{formatMinutes(durationMinutes)}</strong>
          {distanceFromBase != null && (
            <> · Customer is <strong>{distanceFromBase.toFixed(1)} mi</strong> from base</>
          )}
        </div>

        {/* Date picker */}
        <div className="row justify-content-center mb-3">
          <div className="col-md-4">
            <label className="bold d-block text-center">Appointment Date</label>
            <DatePicker
              selected={selectedDate}
              onChange={this.handleDateChange}
              minDate={today}
              filterDate={(date) => isDateAvailable(date, this.state.closedDaysOfWeek, this.state.blockedDates)}
              excludeDates={this.state.blockedDates.map(d => moment(d).toDate())}
              dateFormat="EEEE, MMMM d, yyyy"
              className="form-control text-center"
            />
          </div>
        </div>

        {/* Slot grid */}
        <div className="row justify-content-center">
          <div className="col-md-8">
            <label className="bold d-block text-center mb-2">
              {forceBook || serverSlots.length === 0
                ? 'All operating-hour times'
                : 'Available start times'}
            </label>

            {loading && (
              <div className="text-center text-muted">
                <i className="fa-sharp fa-solid fa-spinner fa-spin mr-2" />Checking availability…
              </div>
            )}

            {!loading && error && (
              <div className="alert alert-light border" style={{ fontSize: 13 }} role="alert">
                <i className="fa-sharp fa-regular fa-circle-info mr-2" />
                {error}
              </div>
            )}

            {!loading && hasServerData && serverSlots.length === 0 && !forceBook && (
              <div className="alert alert-warning text-center">
                <i className="fa-sharp fa-regular fa-calendar-xmark mr-2" />
                No slots fit a {formatMinutes(durationMinutes)} service on this date.
                Pick a different date.
              </div>
            )}

            {!loading && !hasServerData && visibleSlots.length === 0 && (
              <div className="alert alert-warning text-center">
                No available slots on this date. Pick a different date.
              </div>
            )}

            {!loading && visibleSlots.length > 0 && (
              <div className="d-flex flex-wrap justify-content-center" style={{ gap: 8 }}>
                {visibleSlots.map(slot => {
                  const isSelected = slot.start_time === selectedSlot;
                  const isForceSlot = !!slot.fallback && serverSlots.length > 0;
                  // Server marks conflicting slots can_fit:false. Render those
                  // disabled + dimmed in normal mode; allow click in force-book.
                  const conflicts = slot.can_fit === false;
                  const isDisabled = conflicts && !forceBook;
                  let variant = 'btn-outline-secondary';
                  if (isSelected) variant = 'btn-primary';
                  else if (conflicts) variant = 'btn-outline-danger';
                  else if (isForceSlot) variant = 'btn-outline-warning';
                  return (
                    <button
                      key={slot.start_time}
                      type="button"
                      onClick={() => this.handleSlotPick(slot)}
                      className={`btn btn-sm ${variant}`}
                      style={{ minWidth: 90, opacity: isDisabled ? 0.4 : 1 }}
                      disabled={isDisabled}
                      title={
                        conflicts ? 'Conflicts with an existing stop' :
                        isForceSlot ? 'Outside available slots — force book' : undefined
                      }
                    >
                      {formatTimeLabel(slot.start_time)}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Force-book toggle — only meaningful when there are *some* server
                slots and admin wants to expand to outside-available times.
                Hidden when server returned zero slots (day genuinely can't fit). */}
            {!loading && serverSlots.length > 0 && !forceBook && (
              <div className="text-center mt-3">
                <button type="button" className="btn btn-link btn-sm" onClick={this.toggleForceBook}>
                  <i className="fa-sharp fa-regular fa-clock mr-1" />
                  Show all operating-hour times (force book)
                </button>
              </div>
            )}
            {!loading && forceBook && serverSlots.length > 0 && (
              <div className="text-center mt-3">
                <button type="button" className="btn btn-link btn-sm" onClick={this.toggleForceBook}>
                  <i className="fa-sharp fa-regular fa-filter mr-1" />
                  Show only available times
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Selection confirmation */}
        {selectedSlot && (
          <div className="alert alert-info text-center mt-4" style={{ fontSize: 14 }}>
            <i className="fa-sharp fa-regular fa-circle-check mr-2" />
            <strong>{moment(selectedDate).format('dddd, MMMM D')}</strong> at{' '}
            <strong>{formatTimeLabel(selectedSlot)}</strong>
          </div>
        )}
      </div>
    );
  }
}

export default MobileAppointmentForm;
